function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/* eslint-disable max-statements */
import React from 'react';
import classNames from 'classnames';
import { AppError } from 'src/redux/types';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { fetchSaleItemTransactionConfirm, invalidateSellItem, sellItem, fetchHeroDetail, prepareSellItem } from 'src/redux/actions';
import { updateWalletBalance } from '@profile/src/redux/remoteActions';
import PageContent from 'src/Components/PageContent';
import Spinner from 'src/Components/Spinner';
import ErrorMessage from './ErrorMessage';
import styles from './styles.module.scss';
import icClose from 'assets/svg/ic_close.svg';
import { CurrencyType, formatCurrency, normalNumberToSystemCurrency } from '@wolffun/system-currency';
import { logSellHeroEvent } from 'src/utils/analytic';
import { formatErrorMessage, validateInputPrice } from './utils';
import { SellHeroError } from './constants';
import Modal from 'src/Components/Modal';
import PreventSellingModal from '../PreventSellingModal';
import { currencyIconMap, currencyName, HeroStatus } from 'src/utils/types';
import Button from '../../Components/Button';
import ErrorView from 'src/Components/ErrorViewV2';
import { ErrorViewType } from 'src/Components/ErrorViewV2/config';
import { getPreferredWalletProviderName } from 'src/utils/session';
import { useConfirmOpenWalletConnect } from 'src/Components/WalletConnectConfirmProvider';
import { useTestId } from '@wolffun/testable-id';
const SALE_CURRENCY = CurrencyType.THC;

const HeroSaleTransactionModal = ({
  hero,
  onClose,
  onCompleted,
  onFailure
}) => {
  const dispatch = useTypedDispatch();
  const {
    generateTestIdProps
  } = useTestId();
  const {
    confirmOpenWalletConnect
  } = useConfirmOpenWalletConnect();
  const invalidated = useTypedSelector(state => state.saleItemTransaction.invalidated);
  const loading = useTypedSelector(state => state.saleItemTransaction.loading);
  const error = useTypedSelector(state => state.saleItemTransaction.error);
  const transactionFee = useTypedSelector(state => state.saleItemTransaction.transactionFee || 0);
  const thgBalance = useTypedSelector(state => state.connectWallet.thgBalance);
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const userId = useTypedSelector(state => {
    var _state$userProfile$us;

    return (_state$userProfile$us = state.userProfile.user) === null || _state$userProfile$us === void 0 ? void 0 : _state$userProfile$us.id;
  });
  const saleItemLoading = useTypedSelector(state => state.sellItem.loading);
  const saleItemError = useTypedSelector(state => state.sellItem.error);
  const currencyUnit = currencyName[SALE_CURRENCY];
  const [price, setPrice] = React.useState(0);
  const [serviceFee, setServiceFee] = React.useState({
    token: CurrencyType.THC,
    value: '0'
  }); // use serviceFee for displaying data to user only

  const [validateError, setValidateError] = React.useState(SellHeroError.NO_ERROR);
  const [showWaiting, setShowWaiting] = React.useState(false);
  React.useEffect(() => {
    const fee = normalNumberToSystemCurrency(price * transactionFee, SALE_CURRENCY);
    setServiceFee(fee);
  }, [price, transactionFee]);
  React.useEffect(() => {
    if (saleItemLoading) {
      const timeout = setTimeout(() => setShowWaiting(true), 10 * 1000);
      return () => clearTimeout(timeout);
    } else {
      setShowWaiting(false);
      return undefined;
    }
  }, [saleItemLoading]);
  React.useEffect(() => {
    const error = validateInputPrice(price);
    setValidateError(error);
  }, [price]);
  React.useEffect(() => {
    fetchData();
  }, []);
  const {
    current: handleBeforeUnloadEvent
  } = React.useRef(e => {
    e.preventDefault();
    e.returnValue = 'Sale item is still in process. Are you sure you want to leave this page?';
  });
  React.useEffect(() => {
    if (saleItemLoading) {
      window.addEventListener('beforeunload', handleBeforeUnloadEvent);
    }

    return () => window.removeEventListener('beforeunload', handleBeforeUnloadEvent);
  }, [saleItemLoading]);

  const fetchData = () => {
    dispatch(updateWalletBalance());
    dispatch(invalidateSellItem());
    dispatch(fetchSaleItemTransactionConfirm());
  };

  const isItemAvailable = hero.hero.status === HeroStatus.AVAILABLE;

  const onSellButtonPressed = async () => {
    dispatch(invalidateSellItem());
    const salePrice = normalNumberToSystemCurrency(price, SALE_CURRENCY);
    const preparedData = await dispatch(prepareSellItem({
      systemCurrency: salePrice,
      tokenId: Number(hero.hero.tokenId)
    }));
    confirmOpenWalletConnect(async () => {
      const {
        error
      } = await dispatch(sellItem(preparedData));

      if (error !== undefined && error !== AppError.NO_ERROR) {
        onFailure(error);
        dispatch(fetchHeroDetail(hero.hero.id));
        return;
      }

      onCompleted();

      if (thgBalance && thcBalance && userId) {
        logSellHeroEvent({
          THG: thgBalance,
          THC: thcBalance,
          battleCapTHC: hero.hero.battleCapTHC,
          limitTHCBattleCap: hero.hero.totalBattleCapTHC,
          heroLevel: hero.hero.level,
          heroRarity: hero.hero.rarity,
          heroToken: hero.hero.tokenId,
          heroTypeId: hero.hero.heroTypeId,
          price: salePrice,
          skinRarity: hero.hero.skinRarity,
          heroId: hero.hero.id,
          trophyLevel: hero.hero.trophy,
          userId,
          transactionFee: serviceFee
        });
      }
    });
  };

  const renderError = () => saleItemError === AppError.HERO_IN_THE_MATCH ? /*#__PURE__*/React.createElement(PreventSellingModal, {
    onClose: onClose
  }) : /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Sell item"), /*#__PURE__*/React.createElement("button", {
    className: styles.close,
    onClick: onClose
  }, /*#__PURE__*/React.createElement("img", {
    src: icClose
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.placeholder
  }, /*#__PURE__*/React.createElement(ErrorView, {
    config: {
      type: ErrorViewType.Unknown
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose,
    title: "i got it"
  })))));

  const renderLoading = () => /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Sell item"), /*#__PURE__*/React.createElement("button", {
    className: styles.close,
    onClick: onClose
  }, /*#__PURE__*/React.createElement("img", {
    src: icClose
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement(Spinner, null), /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, /*#__PURE__*/React.createElement(Button, {
    title: "Cancel",
    onClick: onClose,
    type: "secondary"
  }), /*#__PURE__*/React.createElement(Button, {
    title: "Sell now",
    disabled: true
  })))));

  const renderContent = () => /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Sell item"), /*#__PURE__*/React.createElement("button", {
    className: styles.close,
    disabled: saleItemLoading,
    onClick: onClose
  }, /*#__PURE__*/React.createElement("img", {
    src: icClose
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.subtitle
  }, `You are about to sell #${hero.hero.tokenId} ${hero.skin.name} ${hero.heroType.name} on the marketplace`), /*#__PURE__*/React.createElement("div", {
    className: styles.priceRow
  }, /*#__PURE__*/React.createElement("input", _extends({
    maxLength: 9,
    onKeyPress: e => {
      const {
        value,
        maxLength
      } = e.currentTarget;

      if (String(value).length >= maxLength) {
        e.preventDefault();
        return;
      }
    },
    className: styles.price,
    type: "number",
    placeholder: 'Type your price…',
    onChange: e => {
      const value = Number(e.target.value) || 0; // input always return string value, need to case to desired type

      setPrice(value);
    }
  }, generateTestIdProps('input-hero-price'))), /*#__PURE__*/React.createElement("div", {
    className: styles.wrapUnit
  }, currencyIconMap[SALE_CURRENCY] && /*#__PURE__*/React.createElement("img", {
    draggable: false,
    src: currencyIconMap[SALE_CURRENCY],
    className: styles.icon
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, currencyUnit))), /*#__PURE__*/React.createElement("span", _extends({
    className: classNames(styles.warning, validateError !== SellHeroError.NO_ERROR ? styles.alert : undefined)
  }, generateTestIdProps('minium-error-price')), formatErrorMessage(validateError, currencyUnit)), /*#__PURE__*/React.createElement("div", {
    className: styles.priceBreakdownRow
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Your balance"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.price
  }, generateTestIdProps('user-balance')), thcBalance && formatCurrency(thcBalance, {
    withCurrency: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.priceBreakdownRow
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Service Fee (", transactionFee * 100, "%)"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.price
  }, generateTestIdProps('service-fee')), formatCurrency(serviceFee, {
    withCurrency: true,
    precision: 8
  }))), showWaiting ? /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.error,
    warning: true,
    title: "It may take a while...",
    description: `We are still waiting for transaction to be processed. Please be patient. You might need to check your ${getPreferredWalletProviderName() || ''} wallet.`
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, /*#__PURE__*/React.createElement(Button, {
    title: "Cancel",
    onClick: onClose,
    disabled: saleItemLoading,
    type: "secondary"
  }), !isItemAvailable || validateError !== SellHeroError.NO_ERROR ? /*#__PURE__*/React.createElement(Button, {
    title: "Sell now",
    disabled: true
  }) : saleItemLoading ? /*#__PURE__*/React.createElement(Button, {
    title: "Processing",
    disabled: true,
    loading: true
  }) : /*#__PURE__*/React.createElement(Button, {
    title: "Sell now",
    onClick: onSellButtonPressed
  })))));

  return /*#__PURE__*/React.createElement(PageContent, {
    invalidated: invalidated,
    loading: loading,
    error: error !== AppError.NO_ERROR || saleItemError !== AppError.NO_ERROR,
    renderContent: renderContent,
    renderLoading: renderLoading,
    renderError: renderError
  });
};

export default HeroSaleTransactionModal;